if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        /* K+K resilience patch: the Optis / BSS Product Options app can
           relocate or rebuild this element's <form> before or after the
           custom element upgrades. The stock constructor crashed on the
           missing form ("Cannot read properties of null"), which left the
           element un-upgraded and broke variant selection (default variant
           added to cart). Instead of assuming the form is present, we
           initialize lazily and retry briefly until the form exists —
           searching by the form's id anywhere in the document if it was
           moved outside this element. */
        this.kkInitialized = false;
        this.kkTryInit();
        if (!this.kkInitialized) {
          let attempts = 0;
          const timer = setInterval(() => {
            attempts++;
            this.kkTryInit();
            if (this.kkInitialized || attempts > 100) clearInterval(timer);
          }, 100);
        }
      }

      kkEnsureLive() {
        /* If Optis replaced (not just moved) the form after we initialized,
           our captured reference points at a detached node. Detect and re-init
           against the live form. */
        if (this.kkInitialized && this.form && this.form.isConnected === false) {
          this.kkInitialized = false;
          this.form = null;
          this.submitButton = null;
          this.submitButtonText = null;
          this.kkTryInit();
        }
      }

      kkResolveCart() {
        /* BSS PO's cart observer can break the cart-drawer / cart-notification
           upgrade the same way it broke this element's. Only accept a cart
           element that actually has the methods we call; otherwise behave as
           if there is no AJAX cart (stock Dawn then redirects to /cart). */
        const candidates = document.querySelectorAll('cart-notification, cart-drawer');
        for (const c of candidates) {
          if (typeof c.getSectionsToRender === 'function' && typeof c.renderContents === 'function') return c;
        }
        return null;
      }

      kkFindForm() {
        return (
          this.querySelector('form') ||
          (this.dataset.sectionId && document.getElementById(`product-form-${this.dataset.sectionId}`)) ||
          null
        );
      }

      kkTryInit() {
        if (this.kkInitialized) return;
        const form = this.kkFindForm();
        if (!form) return;
        const submitButton = this.querySelector('[type="submit"]') || form.querySelector('[type="submit"]');
        const submitButtonText = submitButton && submitButton.querySelector('span');
        if (!form.querySelector('[name=id]') || !submitButton || !submitButtonText) return;

        this.form = form;
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = this.kkResolveCart();
        this.submitButton = submitButton;
        this.submitButtonText = submitButtonText;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.kkInitialized = true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.kkInitialized) this.kkTryInit();
        this.kkEnsureLive();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        try {
          this.kkDoSubmit();
        } catch (error) {
          /* Never strand the customer on a stuck spinner. Reset the button
             and fall back to a plain (non-AJAX) form submission, which adds
             to cart via full page load no matter what apps broke. */
          console.error('[KK product-form] AJAX add-to-cart failed, falling back to native submit:', error);
          this.kkResetButtonState();
          if (this.form) HTMLFormElement.prototype.submit.call(this.form);
        }
      }

      kkResetButtonState() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        (this.querySelector('.loading__spinner') || (this.form && this.form.querySelector('.loading__spinner')))?.classList.add('hidden');
      }

      kkDoSubmit() {

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        (this.querySelector('.loading__spinner') || this.form.querySelector('.loading__spinner'))?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.cart = this.kkResolveCart();
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            (this.querySelector('.loading__spinner') || this.form.querySelector('.loading__spinner'))?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.kkInitialized) this.kkTryInit();
        this.kkEnsureLive();
        if (!this.submitButton || !this.submitButtonText) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
          /* Self-heal: if a previous submit crashed mid-flight, clear the
             stuck loading/spinner state when the button is re-enabled. */
          this.kkResetButtonState();
        }
      }

      get variantIdInput() {
        if (!this.form) this.kkTryInit();
        this.kkEnsureLive();
        return this.form ? this.form.querySelector('[name=id]') : null;
      }
    }
  );
}
